import { useMemo } from 'react';

import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { FieldType } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { useIntl } from 'react-intl';

import { type ClinicSize } from '../../../../../contracts';
import { getOnboardingProfileHost } from '../../../onboarding-profile-host';
import {
  OnboardingCombobox,
  type OnboardingComboboxItem,
} from '../shared/dropdowns/OnboardingCombobox';
import { OnboardingStepShell } from '../shared/OnboardingStepShell';
import { onboardingToggleButtonClassName } from '../shared/onboardingToggleButtonClassName';
import { personalizationSharedMessages } from '../shared/personalization-shared.messages';
import { clinicStepMessages } from './clinic-step.messages';

const CLINIC_SIZES: Array<{ value: ClinicSize; labelKey: TranslationKey }> = [
  { value: 'only-me', labelKey: 'signup.clinicSizeOnlyMe' },
  { value: '2-10', labelKey: 'signup.clinicSize2To10' },
  { value: '10-50', labelKey: 'signup.clinicSize10To50' },
  { value: '50-100', labelKey: 'signup.clinicSize50To100' },
  { value: 'over-100', labelKey: 'signup.clinicSizeOver100' },
];

interface ClinicStepProps {
  clinicName: string;
  country: CountryType | null;
  clinicSize: ClinicSize | null;
  emrEhr: string | null;
  onClinicNameChange: (value: string) => void;
  onOfficialPracticeChange: (isOfficial: boolean, practiceId?: string) => void;
  onClinicSizeChange: (value: ClinicSize) => void;
  onEmrEhrChange: (value: string | null) => void;
  onContinue: () => void;
  isValid: boolean;
  /** Optional slot rendered below the EHR combobox (e.g. book-a-demo in learn-basics flow). */
  bookDemoSlot?: React.ReactNode;
  /**
   * Pins Continue to the bottom of the column and scrolls the fields beneath it,
   * instead of letting the CTA ride below the content. On for the learn-basics
   * flow, whose later steps already pin their actions — the v2 flow keeps its
   * grow-and-scroll column so its own steps stay consistent with each other.
   */
  hasPinnedFooter?: boolean;
}

export function ClinicStep({
  clinicName,
  country,
  clinicSize,
  emrEhr,
  onClinicNameChange,
  onOfficialPracticeChange,
  onClinicSizeChange,
  onEmrEhrChange,
  onContinue,
  isValid,
  bookDemoSlot,
  hasPinnedFooter = false,
}: ClinicStepProps) {
  const {
    CQCPracticeAutocomplete,
    integrationProviderOtherId,
    isUkPracticeSearchEnabled,
    useIsCountrySpecificEhrSelectionEnabled,
    useIsWorkspaceOnboardingRenameEnabled,
    useOnboardingFieldTracking,
    useQueryIntegrationProviders,
    useSelectedEmrOption,
  } = getOnboardingProfileHost();
  const { t } = useTranslations();
  const intl = useIntl();
  const isWorkspaceName = useIsWorkspaceOnboardingRenameEnabled();
  const { trackImmediate, trackTextDebounced } = useOnboardingFieldTracking('clinic');
  const showCQCPracticeSearch =
    country?.countryCode === 'GB' && isUkPracticeSearchEnabled();
  const clinicLabel = showCQCPracticeSearch
    ? intl.formatMessage(clinicStepMessages.practiceFieldLabel)
    : isWorkspaceName
      ? intl.formatMessage(clinicStepMessages.workspaceNameLabel)
      : t('signup.clinicName');

  const isCountryScopedEhrList = useIsCountrySpecificEhrSelectionEnabled();
  const countryCode = country?.countryCode;
  const providersQuery = useQueryIntegrationProviders({
    country: countryCode,
    enabled: isCountryScopedEhrList && !!countryCode,
  });
  const selectedEmr = useSelectedEmrOption(emrEhr, countryCode);

  const ehrProviderItems: OnboardingComboboxItem[] = useMemo(() => {
    if (isCountryScopedEhrList) {
      // Options appear only once the country-scoped fetch settles: no enum
      // fallback, and no Other-only bias while loading.
      if (!providersQuery.isSuccess) return [];
      return [
        ...providersQuery.data.map((provider) => ({
          // Display name doubles as the persisted value.
          value: provider.name,
          label: provider.name,
          isPro: false,
        })),
        {
          value: integrationProviderOtherId,
          label: intl.formatMessage(personalizationSharedMessages.other),
          isPro: false,
        },
      ];
    }
    return Object.values(EHRProvider).map((provider) => ({
      value: provider,
      label:
        provider === EHRProvider.Other
          ? intl.formatMessage(personalizationSharedMessages.other)
          : provider,
      isPro: false,
    }));
  }, [
    isCountryScopedEhrList,
    providersQuery.isSuccess,
    providersQuery.data,
    integrationProviderOtherId,
    intl,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onContinue();
    }
  };

  const handleClinicNameChange = (value: string) => {
    if (value.trim() !== '') trackTextDebounced('organisation_name');
    onClinicNameChange(value);
  };

  const handleClinicSizeChange = (value: ClinicSize) => {
    trackImmediate('clinic_size', FieldType.Button);
    onClinicSizeChange(value);
  };

  const handleEmrEhrChange = (value: string | undefined) => {
    if (value) trackImmediate('ehr_provider', FieldType.Dropdown, value);
    onEmrEhrChange(value ?? null);
  };

  const emrHasIntegration = !!selectedEmr?.icon;
  const emrDisplayName = selectedEmr?.name ?? '';
  // With the country-scoped list on, there is no list to offer until a country
  // is picked (geo detection can return none), so hide the optional field
  // rather than render an empty, unexplained dropdown.
  const showEmrField = !isCountryScopedEhrList || !!countryCode;

  const hasClinicName = clinicName.trim() !== '';

  const continueButton = (
    <Button size="lg" type="submit" disabled={!isValid} className="w-full">
      {t('common.continue')}
    </Button>
  );

  const body = (
    <>
      <div className="flex flex-col gap-4">
        <TypographyH2>
          {isWorkspaceName
            ? intl.formatMessage(clinicStepMessages.heading)
            : t('signup.tellUsAboutYourClinic')}
        </TypographyH2>
        <TypographyP className="hidden lg:block">
          {t('signup.clinicSubtitle')}
        </TypographyP>
      </div>

      <div className="flex flex-col gap-4 lg:gap-6">
        <div className="flex flex-col gap-1.5">
          <TypographyP2 className="font-medium">{clinicLabel}</TypographyP2>
          {showCQCPracticeSearch ? (
            <CQCPracticeAutocomplete
              value={clinicName}
              onValueChange={handleClinicNameChange}
              onPracticeSelect={onOfficialPracticeChange}
              placeholder={intl.formatMessage(
                clinicStepMessages.practiceSearchPlaceholder
              )}
            />
          ) : (
            <Input
              id="clinicName"
              value={clinicName}
              onChange={(e) => handleClinicNameChange(e.target.value)}
              placeholder={
                isWorkspaceName
                  ? intl.formatMessage(clinicStepMessages.workspaceNamePlaceholder)
                  : t('signup.clinicPlaceholder')
              }
              autoComplete="organization"
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <TypographyP2 className="font-medium">
            {t('signup.howManyClinicians')}
          </TypographyP2>
          <div className="grid grid-cols-3 gap-2 lg:flex lg:flex-row">
            {CLINIC_SIZES.map((size) => (
              <Button
                key={size.value}
                type="button"
                variant="outline"
                size="md"
                onClick={() => handleClinicSizeChange(size.value)}
                className={onboardingToggleButtonClassName(
                  clinicSize === size.value,
                  'w-full lg:w-auto'
                )}
              >
                {t(size.labelKey)}
              </Button>
            ))}
          </div>
        </div>

        {/* EMR/EHR and the book-a-demo slot used to be gated on `clinicSize`
            as a progressive-disclosure cue. Now that clinic size is optional,
            we gate on `clinicName` instead so users who choose to skip the
            size question still see — and can opt into — EMR/EHR and demo. */}
        {hasClinicName && showEmrField && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5">
              <TypographyP2 className="font-medium">
                {t('signup.emrEhrOptional')}
              </TypographyP2>
              <OnboardingCombobox
                items={ehrProviderItems}
                value={emrEhr ?? undefined}
                onSelect={handleEmrEhrChange}
                selectItemMsg={t('signup.selectEmrEhr')}
                searchPlaceholder={t('signup.searchEmrEhr')}
                unselect
                unselectMsg={t('signup.emrNone')}
                className="rounded-md bg-surface hover:bg-surface-hover data-[state=open]:border-border-strong"
                drawerTitle={t('signup.emrEhrOptional')}
              />
            </div>
            {isCountryScopedEhrList && providersQuery.isError && (
              <TypographyCaption className="pl-0.5 text-validation-destructive">
                {intl.formatMessage(clinicStepMessages.ehrListLoadError)}
              </TypographyCaption>
            )}
            {emrEhr && emrHasIntegration && (
              <TypographyCaption className="pl-0.5 text-text-secondary">
                {t('signup.emrIntegrationCaption', { emr: emrDisplayName })}
              </TypographyCaption>
            )}
          </div>
        )}

        {bookDemoSlot && hasClinicName && <div className="pt-2">{bookDemoSlot}</div>}
      </div>
    </>
  );

  if (hasPinnedFooter) {
    return (
      <OnboardingStepShell
        onSubmit={handleSubmit}
        contentClassName="gap-6 lg:gap-10"
        footer={continueButton}
      >
        {body}
      </OnboardingStepShell>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-6 lg:gap-10">
      {body}

      {/* Continue button - pushed to bottom */}
      <div className="flex flex-1 items-end pt-4">{continueButton}</div>
    </form>
  );
}
